import { forwardRef, type ElementRef, type ComponentPropsWithoutRef } from 'react'
import { DialogTrigger as PopoverTrigger, Dialog as DialogPrimitive, Popover as PopoverPrimitive, OverlayArrow, HeadingContext, useContextProps } from 'react-aria-components'
import { cn } from '#app/utils/tailwind-merge.ts'
import { H2 } from '../typography/h2.tsx'

export interface PopoverProps extends ComponentPropsWithoutRef<typeof DialogPrimitive> {
	offset?: `${number}`
	crossOffset?: `${number}`
	side?: 'bottom' | 'top' | 'left' | 'right'
}

/**
 * Popover component.
 *
 * @component
 * @example
 * ```jsx
 * <PopoverTrigger>
 * 	<Button>Create account</Button>
 * 	<Popover>
 * 		{({ close }) => (
 * 			<>
 * 				<PopoverTitle>Create account</PopoverTitle>
 * 				<Flex justify="end" gap="2" className="mt-6">
 * 					<Button variant="secondary" onPress={close}>Cancel</Button>
 * 					<Button variant="primary" onPress={close}>Create</Button>
 * 				</Flex>
 * 			</>
 * 		)}
 * 	</Popover>
 * </PopoverTrigger>
 * ```
 *
 * @param {offset} [offset] - Spacing between the popover and the trigger across the main axis.
 * @param {crossOffset} [crossOffset] - Spacing between the popover and the trigger across the cross axis.
 * @param {string} [side='bottom'] - Side of the screen to anchor the popover to. Can be `bottom` (default), `top`, `left`, or `right`.
 * @param {ReactNode | (opts: DialogRenderProps) => ReactNode} [children] - Children of the dialog. A function may be provided to access a function to close the dialog.
 * @param {string} [className] - Additional CSS class names.
 * @param {React.HTMLAttributes<HTMLDivElement>} [props] - Additional HTML attributes.
 * @returns {JSX.Element} The Popover component.
 */
const Popover = forwardRef<ElementRef<typeof DialogPrimitive>, PopoverProps>(({ offset, crossOffset, side = 'bottom', className, ...props }, forwardedRef) => {
	const placement =
		side === 'left' ? 'start'
		: side === 'right' ? 'end'
		: side
	return (
		<PopoverPrimitive placement={placement} className={cn('popover', className)} offset={offset ? Number(offset) : undefined} crossOffset={crossOffset ? Number(crossOffset) : undefined}>
			<OverlayArrow className="popover-arrow">
				<svg width="12" height="12" viewBox="0 0 12 12">
					<path d="M0 0 L6 6 L12 0" />
				</svg>
			</OverlayArrow>
			<DialogPrimitive ref={forwardedRef} {...props} />
		</PopoverPrimitive>
	)
})
Popover.displayName = 'Popover'

const PopoverTitle = forwardRef<ElementRef<typeof H2>, ComponentPropsWithoutRef<typeof H2>>((props, ref) => {
	;[props, ref] = useContextProps(props, ref, HeadingContext)
	return <H2 ref={ref} slot="title" {...props} />
})
PopoverTitle.displayName = 'PopoverTitle'

export { PopoverTrigger, Popover, PopoverTitle }
